import LightCustomButton from "@components/buttons/LightCustomButton";
import DataPill from "@components/pills/DataPill";
import LabelRow from "@components/rowcomponents/LabelRow";
import { kycV2Service } from "@services/apis/KycV2";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import {
  getKycV2ModuleStatusFromDetail,
  getKycV2OverallStatus,
  KYC_V2_MODULES,
  KycV2DetailRecord,
  KycV2Module,
} from "@services/models/Kyc/KycV2Models";
import React, { useCallback, useEffect, useState } from "react";
import useSnackbarMessage from "../../utils/HandleSuccessErrorSnackbars";
import ConstantFunctions from "../../utils/constants_functions";
import SettledAccountReponseSlider from "../settleaccounts/components/SettledAccountReponseSlider";

interface KycV2ApprovalDetailsProps {
  userId: number;
  onRefresh: () => void;
}

const StatusBadge: React.FC<{ status?: string | null }> = ({ status }) => {
  return <DataPill value={status} />;
};

const DetailsCard = ({ children }: { children: React.ReactNode }) => (
  <div className="flex flex-col py-[8px] px-[12px] space-y-[12px] border border-border-subtle bg-bg-base rounded-lg">
    {children}
  </div>
);

const KycV2ApprovalDetailsSkeleton = () => (
  <div
    className="flex flex-col gap-[12px] overflow-auto"
    style={{ height: "80vh" }}
    aria-busy="true"
    aria-label="Loading KYC V2 record"
  >
    <DetailsCard>
      <Skeleton height={16} width={140} />
      {Array.from({ length: 6 }, (_, index) => (
        <Skeleton key={index} height={12} />
      ))}
      <div className="border-b border-border-subtle" />
      {Array.from({ length: 5 }, (_, index) => (
        <Skeleton key={`meta-${index}`} height={12} />
      ))}
    </DetailsCard>

    <DetailsCard>
      <Skeleton height={16} width={120} />
      <Skeleton height={12} />
    </DetailsCard>

    <DetailsCard>
      <Skeleton height={16} width={70} />
      <div className="grid grid-cols-1 gap-3">
        {Array.from({ length: 4 }, (_, index) => (
          <Skeleton key={index} height={100} borderRadius={8} />
        ))}
      </div>
    </DetailsCard>

    <DetailsCard>
      <Skeleton height={16} width={140} />
      {Array.from({ length: 4 }, (_, index) => (
        <Skeleton key={index} height={12} />
      ))}
      {Array.from({ length: 4 }, (_, index) => (
        <Skeleton key={`action-${index}`} height={28} borderRadius={8} />
      ))}
    </DetailsCard>

    {Array.from({ length: 5 }, (_, index) => (
      <DetailsCard key={index}>
        <div className="flex flex-row justify-between items-center">
          <Skeleton height={16} width={110} />
          <Skeleton height={20} width={72} borderRadius={999} />
        </div>
        <Skeleton height={12} />
        <Skeleton height={32} borderRadius={6} />
        <div className="grid grid-cols-2 gap-2">
          {Array.from({ length: 4 }, (_, buttonIndex) => (
            <Skeleton
              key={buttonIndex}
              height={28}
              borderRadius={8}
            />
          ))}
        </div>
      </DetailsCard>
    ))}
  </div>
);

const KycV2ApprovalDetails: React.FC<KycV2ApprovalDetailsProps> = ({
  userId,
  onRefresh,
}) => {
  const constantFunctions = new ConstantFunctions();
  const { handleSuccess, handleError } = useSnackbarMessage();

  const [detail, setDetail] = useState<KycV2DetailRecord | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [rejectReasons, setRejectReasons] = useState<
    Partial<Record<KycV2Module, string>>
  >({});
  const [showDumpModal, setShowDumpModal] = useState<boolean>(false);
  const [modalData, setModalData] = useState<any>(null);
  const [modalTitle, setModalTitle] = useState<string>("");

  const fetchRecord = useCallback(async () => {
    try {
      setLoading(true);
      const response = await kycV2Service.fetchRecord(userId);
      setDetail(response.data ?? null);
    } catch (err: any) {
      handleError({
        message: `Failed to fetch KYC V2 record: ${
          err?.response?.data?.msg || err.message || "Unknown error"
        }`,
      });
      setDetail(null);
    } finally {
      setLoading(false);
    }
    // handleError is intentionally omitted — snackbar helpers are not memoized
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  useEffect(() => {
    fetchRecord();
  }, [fetchRecord]);

  const handleShowDump = (dumpData: any, title: string) => {
    setModalData(dumpData);
    setModalTitle(title);
    setShowDumpModal(true);
  };

  const runModuleAction = async (
    action: "approve" | "reject" | "needsReview" | "reset",
    module: KycV2Module,
  ) => {
    const actionKey = `${action}-${module}`;
    try {
      setActionLoading(actionKey);

      if (action === "approve") {
        await kycV2Service.approveModule({ userId, module });
      } else if (action === "reject") {
        await kycV2Service.rejectModule({
          userId,
          module,
          reason: rejectReasons[module]?.trim() || undefined,
        });
      } else if (action === "needsReview") {
        await kycV2Service.needsReviewModule({ userId, module });
      } else {
        await kycV2Service.resetModule({ userId, module });
      }

      handleSuccess({
        message: `${module} marked as ${
          action === "needsReview"
            ? "needs_review"
            : action === "reject"
              ? "declined"
              : action === "reset"
                ? "pending"
                : "approved"
        }`,
      });

      await fetchRecord();
      onRefresh();
    } catch (err: any) {
      handleError({
        message: `Failed to update ${module}: ${
          err?.response?.data?.msg || err.message || "Unknown error"
        }`,
      });
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return <KycV2ApprovalDetailsSkeleton />;
  }

  if (!detail) {
    return (
      <div className="flex items-center justify-center h-40">
        <p className="text-text-subtle text-[12px]">No KYC V2 record found</p>
      </div>
    );
  }

  const raw = detail.raw;
  const basicData = detail.modules?.BASIC?.data;
  const firstName = basicData?.firstName ?? raw?.firstName;
  const lastName = basicData?.lastName ?? raw?.lastName;
  const email = basicData?.email ?? raw?.email;
  const dateOfBirth = basicData?.dateOfBirth ?? raw?.dateOfBirth;
  const userCountry =
    basicData?.userCountry ?? raw?.userCountry ?? raw?.country;
  const fullName = `${firstName ?? ""} ${lastName ?? ""}`.trim() || "- -";
  const address =
    [
      raw?.addressLine1,
      raw?.addressLine2,
      raw?.city,
      raw?.stateProvinceRegion,
      raw?.postalCode,
      raw?.country || raw?.userCountry,
    ]
      .filter(Boolean)
      .join(", ") || "- -";

  return (
    <div
      className="flex flex-col gap-[12px] overflow-auto"
      style={{ height: "80vh" }}
    >
      <div className="flex flex-col py-[8px] px-[12px] space-y-[12px] border border-border-subtle bg-bg-base rounded-lg">
        <p className="text-text-base text-[14px] font-medium">User Information</p>
        <LabelRow label="Name" value={fullName} />
        <LabelRow label="Email" value={email || "- -"} />
        <LabelRow label="User ID" value={String(detail.userId)} />
        <LabelRow label="Phone" value={raw?.phone || "- -"} />
        <LabelRow label="Country" value={userCountry || "- -"} />
        <LabelRow
          label="Country of Birth"
          value={basicData?.countryOfBirth || raw?.countryOfBirth || "- -"}
        />
        <LabelRow
          label="Tax ID"
          value={basicData?.taxId || raw?.taxId || "- -"}
        />
        <div className="border-b border-border-subtle"></div>
        <LabelRow
          label="Date of Birth"
          value={
            dateOfBirth
              ? constantFunctions.dateFormatInDDMMYY(dateOfBirth)
              : "- -"
          }
        />
        <LabelRow
          label="Created At"
          value={
            raw?.createdAt
              ? constantFunctions.formatDateTime(raw.createdAt)
              : "- -"
          }
        />
        <LabelRow
          label="Updated At"
          value={
            raw?.updatedAt
              ? constantFunctions.formatDateTime(raw.updatedAt)
              : "- -"
          }
        />
        <LabelRow
          label="Wallet Ops Allowed"
          value={detail.walletOpsAllowed ? "Yes" : "No"}
        />
      </div>

      <div className="flex flex-col py-[8px] px-[12px] space-y-[12px] border border-border-subtle bg-bg-base rounded-lg">
        <p className="text-text-base text-[14px] font-medium">Overall Status</p>
        <div className="flex flex-row justify-between items-center">
          <span className="text-text-base text-[12px] font-normal">
            Overall KYC Status
          </span>
          <StatusBadge status={getKycV2OverallStatus(detail)} />
        </div>
        {detail.kycCompletedAt && (
          <LabelRow
            label="KYC Completed At"
            value={constantFunctions.formatDateTime(detail.kycCompletedAt)}
          />
        )}
      </div>

      <div className="flex flex-col py-[8px] px-[12px] space-y-[12px] border border-border-subtle bg-bg-base rounded-lg">
        <p className="text-text-base text-[14px] font-medium">Images</p>
        <div className="grid grid-cols-1 gap-3">
          {[
            { label: "POI Image", src: raw?.poiImageUrl },
            { label: "Selfie", src: raw?.selfieImageUrl },
            { label: "POA Front", src: raw?.poaImageFrontUrl },
            { label: "POA Back", src: raw?.poaImageBackUrl },
          ].map((image) =>
            image.src ? (
              <div key={image.label} className="flex flex-col items-center">
                <p className="text-text-subtle text-[10px] mb-1">{image.label}</p>
                <img
                  src={image.src}
                  alt={image.label}
                  className="w-full object-contain border border-border-subtle rounded-md"
                  style={{ maxHeight: "150px" }}
                />
              </div>
            ) : (
              <div
                key={image.label}
                className="flex flex-col items-center justify-center h-[100px] bg-gray-50 border border-border-subtle rounded-md"
              >
                <p className="text-text-subtle text-[10px]">
                  {image.label} Not Available
                </p>
              </div>
            ),
          )}
        </div>
      </div>

      <div className="flex flex-col py-[8px] px-[12px] space-y-[12px] border border-border-subtle bg-bg-base rounded-lg">
        <p className="text-text-base text-[14px] font-medium">Document Details</p>
        <LabelRow label="POI Type" value={raw?.poiType || "- -"} />
        <LabelRow label="POI Number" value={raw?.poiNumber || "- -"} />
        <LabelRow label="POA Type" value={raw?.poaType || "- -"} />
        <LabelRow
          label="POA Number"
          value={raw?.poaNumber != null ? String(raw.poaNumber) : "- -"}
        />
        <div className="flex flex-col gap-[4px]">
          <span className="text-text-base text-[12px] font-normal">Address</span>
          <span className="font-normal text-[12px] text-text-base break-words whitespace-normal">
            {address}
          </span>
        </div>
        {(raw?.faceMatchScore != null || raw?.livenessScore != null) && (
          <>
            <div className="border-b border-border-subtle"></div>
            {raw?.faceMatchScore != null && (
              <LabelRow
                label="Face Match Score"
                value={String(raw.faceMatchScore)}
              />
            )}
            {raw?.livenessScore != null && (
              <LabelRow
                label="Liveness Score"
                value={String(raw.livenessScore)}
              />
            )}
          </>
        )}
        {raw?.sessionUrl && (
          <LabelRow
            label="Liveness Session"
            value={raw.sessionUrl}
            isRedirect
            redirectLink={raw.sessionUrl}
          />
        )}
        <div className="flex flex-col gap-2 pt-1">
          <LightCustomButton
            text="View POI Dump"
            isActive={true}
            onClick={() => handleShowDump(raw?.poiDump, "POI Dump")}
            textSize="11px"
            className="!h-[28px]"
          />
          <LightCustomButton
            text="View POA Dump"
            isActive={true}
            onClick={() => handleShowDump(raw?.poaDump, "POA Dump")}
            textSize="11px"
            className="!h-[28px]"
          />
          <LightCustomButton
            text="View AML Dump"
            isActive={true}
            onClick={() => handleShowDump(raw?.amlDump, "AML Dump")}
            textSize="11px"
            className="!h-[28px]"
          />
          <LightCustomButton
            text="View Raw Record"
            isActive={true}
            onClick={() => handleShowDump(raw, "Raw KYC V2 Record")}
            textSize="11px"
            className="!h-[28px]"
          />
        </div>
      </div>

      {KYC_V2_MODULES.map(({ key, label }) => {
        const moduleInfo = detail.modules?.[key];
        const status = getKycV2ModuleStatusFromDetail(detail, key);
        const provider = moduleInfo?.serviceProvider;
        const completedAt = moduleInfo?.completedAt;
        const updatedAt = moduleInfo?.updatedAt;

        return (
          <div
            key={key}
            className="flex flex-col py-[8px] px-[12px] space-y-[10px] border border-border-subtle bg-bg-base rounded-lg"
          >
            <div className="flex flex-row justify-between items-center">
              <p className="text-text-base text-[14px] font-medium">{label}</p>
              <StatusBadge status={status} />
            </div>

            {provider && <LabelRow label="Provider" value={provider} />}
            {completedAt && (
              <LabelRow
                label="Completed At"
                value={constantFunctions.formatDateTime(completedAt)}
              />
            )}
            {updatedAt && (
              <LabelRow
                label="Updated At"
                value={constantFunctions.formatDateTime(updatedAt)}
              />
            )}

            {key === "BASIC" && moduleInfo?.data && (
              <>
                <div className="border-b border-border-subtle"></div>
                <LabelRow
                  label="First Name"
                  value={moduleInfo.data.firstName || "- -"}
                />
                <LabelRow
                  label="Last Name"
                  value={moduleInfo.data.lastName || "- -"}
                />
                <LabelRow label="Email" value={moduleInfo.data.email || "- -"} />
                <LabelRow
                  label="Country"
                  value={moduleInfo.data.userCountry || "- -"}
                />
                <LabelRow
                  label="Tax ID"
                  value={moduleInfo.data.taxId || "- -"}
                />
              </>
            )}

            <div>
              <label className="block text-[11px] text-text-subtle mb-1">
                Reject reason (optional)
              </label>
              <input
                type="text"
                className="w-full border border-border-subtle rounded-md px-2 py-1.5 text-[11px] focus:outline-none focus:ring-1 focus:ring-primary"
                value={rejectReasons[key] || ""}
                onChange={(e) =>
                  setRejectReasons((prev) => ({
                    ...prev,
                    [key]: e.target.value,
                  }))
                }
                placeholder="Reason for rejection"
              />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <LightCustomButton
                text="Approve"
                isActive={true}
                onClick={() => runModuleAction("approve", key)}
                isButtonLoading={actionLoading === `approve-${key}`}
                textSize="11px"
                className="!h-[28px]"
              />
              <LightCustomButton
                text="Reject"
                isActive={true}
                onClick={() => runModuleAction("reject", key)}
                isButtonLoading={actionLoading === `reject-${key}`}
                textSize="11px"
                className="!h-[28px]"
              />
              <LightCustomButton
                text="Needs Review"
                isActive={true}
                onClick={() => runModuleAction("needsReview", key)}
                isButtonLoading={actionLoading === `needsReview-${key}`}
                textSize="11px"
                className="!h-[28px]"
              />
              <LightCustomButton
                text="Reset"
                isActive={true}
                onClick={() => runModuleAction("reset", key)}
                isButtonLoading={actionLoading === `reset-${key}`}
                textSize="11px"
                className="!h-[28px]"
              />
            </div>
          </div>
        );
      })}

      <SettledAccountReponseSlider
        isOpen={showDumpModal}
        onClose={() => setShowDumpModal(false)}
        data={modalData}
        isSuccess={true}
        title={modalTitle}
      />
    </div>
  );
};

export default KycV2ApprovalDetails;
